import React, { useState, useEffect } from 'react';
import { Routes, Route, Navigate, useLocation } from 'react-router-dom';
import Header from './components/Header';
import BookingCalendar from './components/BookingCalendar';
import BookingForm from './components/BookingForm';
import AuthForm from './components/AuthForm';
import './App.css';

function App() {
  const [bookings, setBookings] = useState([]);
  const [currentUser, setCurrentUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const location = useLocation();

  const API_URL = window.location.hostname === 'localhost'
    ? 'http://localhost:3001'
    : `https://${window.location.hostname}`;

  // All API calls go through this so cookies (the new auth source of
  // truth) are always sent, and so we still attach the legacy
  // localStorage token during the rollout window.
  const authedFetch = (path, options = {}) => {
    const token = localStorage.getItem('footballBookingToken');
    const headers = { ...(options.headers || {}) };
    if (token && !headers.Authorization) headers.Authorization = `Bearer ${token}`;
    return fetch(`${API_URL}${path}`, {
      ...options,
      credentials: 'include',
      headers,
    });
  };

  useEffect(() => {
    // Validate the session against the cookie. /api/me also accepts the
    // legacy header-token, so users currently logged in via localStorage
    // are seamlessly migrated to a cookie session.
    const bootstrap = async () => {
      try {
        const res = await authedFetch('/api/me');
        if (res.ok) {
          const data = await res.json();
          setCurrentUser(data.user);
          // Refresh the localStorage copy so the user object stays
          // current (role/name changes etc).
          localStorage.setItem('footballBookingUser', JSON.stringify(data.user));
          if (data.token) localStorage.setItem('footballBookingToken', data.token);
          await fetchBookings();
        } else {
          // No valid session — clear any stale localStorage so we don't
          // keep showing a phantom "logged in" header on reload.
          localStorage.removeItem('footballBookingUser');
          localStorage.removeItem('footballBookingToken');
          setCurrentUser(null);
        }
      } catch (err) {
        console.error('Session bootstrap failed:', err);
      } finally {
        setLoading(false);
      }
    };
    bootstrap();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchBookings = async () => {
    try {
      const response = await authedFetch('/api/bookings');
      if (response.ok) {
        const data = await response.json();
        setBookings(data);
      } else {
        console.error('Failed to fetch bookings, status:', response.status);
      }
    } catch (error) {
      console.error('Error fetching bookings:', error);
    }
  };

  const handleLogin = async (credentials) => {
    try {
      const response = await fetch(`${API_URL}/api/login`, {
        method: 'POST',
        // credentials: 'include' so the Set-Cookie from the server
        // actually lands in the browser jar.
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(credentials),
      });

      if (!response.ok) {
        console.error('Login failed, status:', response.status);
        return false;
      }

      const data = await response.json();
      setCurrentUser(data.user);
      localStorage.setItem('footballBookingUser', JSON.stringify(data.user));
      if (data.token) localStorage.setItem('footballBookingToken', data.token);

      await fetchBookings();
      return true;
    } catch (error) {
      console.error('Login error:', error);
      return false;
    }
  };

  const handleLogout = async () => {
    try {
      // Tell the server to clear the cookie. Fire-and-forget — even if
      // the network drops we still want to wipe local state.
      await authedFetch('/api/logout', { method: 'POST' });
    } catch (err) {
      console.error('Logout request failed (clearing local state anyway):', err);
    }
    setCurrentUser(null);
    setBookings([]);
    localStorage.removeItem('footballBookingUser');
    localStorage.removeItem('footballBookingToken');
  };
  
  const handleAddBooking = async (newBooking) => {
    try {
      const bookingData = {
        date: newBooking.date,
        startTime: newBooking.startTime,
        endTime: newBooking.endTime,
        pitchId: newBooking.pitchId || getPitchIdFromName(newBooking.pitch),
        pitchPortion: newBooking.pitch === 'Gym' ? 'full' : (newBooking.pitchPortion || 'full'),
        sessionType: newBooking.sessionType,
        notes: newBooking.notes || ''
      };

      const response = await authedFetch('/api/bookings', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(bookingData),
      });

      if (!response.ok) {
        // Surface the actual server message so failures aren't a black box.
        let serverError = '';
        try {
          const data = await response.json();
          serverError = data?.error || '';
        } catch (_) {
          try { serverError = await response.text(); } catch (__) {}
        }
        console.error('Add booking failed:', response.status, serverError);

        if (response.status === 401 || response.status === 403) {
          // Session is gone — push the user to login rather than leaving
          // them stuck on a form they can't submit.
          setCurrentUser(null);
          localStorage.removeItem('footballBookingUser');
          localStorage.removeItem('footballBookingToken');
          return {
            ok: false,
            error: serverError || 'Your session has expired. Please sign in again.',
          };
        }
        return {
          ok: false,
          error: serverError || `Server returned ${response.status}. Please try again.`,
        };
      }

      await fetchBookings();
      return { ok: true };
    } catch (error) {
      console.error('Error adding booking:', error);
      return {
        ok: false,
        error: `Could not reach the server (${error.message || 'network error'}). Check your connection and try again.`,
      };
    }
  };

  // Helper function to get pitch ID from pitch name
  const getPitchIdFromName = (pitchName) => {
    switch(pitchName) {
      case 'Taylorstown Pitch':
        return 1;
      case 'Loughbeg Road Pitch':
        return 2;
      case 'Gym':
        return 3;
      default:
        console.warn('Unknown pitch name:', pitchName);
        return 1; // Default to first pitch
    }
  };

  // Show loading indicator while checking authentication
  if (loading) {
    return <div className="loading">Loading...</div>;
  }
  
  return (
    <div>
      <Header currentUser={currentUser} onLogout={handleLogout} />
      <div className="container">
        <Routes>
          <Route 
            path="/" 
            element={
              currentUser ? (
                <BookingCalendar 
                  bookings={bookings} 
                  currentUser={currentUser}
                />
              ) : (
                <Navigate to="/login" state={{ from: location }} replace />
              )
            } 
          />
          
          <Route 
            path="/add-booking" 
            element={
              currentUser?.role === 'coach' ? (
                <BookingForm 
                  onAddBooking={handleAddBooking} 
                  existingBookings={bookings}
                />
              ) : (
                <Navigate to="/login" state={{ from: location }} replace />
              )
            } 
          />
          
          <Route 
            path="/login" 
            element={
              currentUser ? (
                <Navigate to="/" replace />
              ) : (
                <AuthForm onLogin={handleLogin} />
              )
            } 
          />
          <Route 
            path="*" 
            element={
              currentUser ? <Navigate to="/" replace /> : <Navigate to="/login" replace />
            } 
          />
        </Routes>
      </div>
    </div>
  );
}

export default App;